import {
  DateSubcondition,
  DateSubconditionType,
} from "@redotech/redo-model/return-flow/date-subcondition";

export interface DateSubconditionState {
  type: DateSubconditionType;
  beforeDate: Date | null;
  afterDate: Date | null;
  startDate: Date | null;
  endDate: Date | null;
  days: number | null;
}

export const emptyDateSubconditionState: DateSubconditionState = {
  type: DateSubconditionType.ALL_TIME,
  beforeDate: null,
  afterDate: null,
  startDate: null,
  endDate: null,
  days: 30,
};

export const validateDateSubcondition = (
  state: DateSubconditionState,
): boolean => {
  switch (state.type) {
    case DateSubconditionType.ON_OR_BEFORE:
      return !!state.beforeDate;
    case DateSubconditionType.ON_OR_AFTER:
      return !!state.afterDate;
    case DateSubconditionType.BETWEEN:
      return !!state.startDate && !!state.endDate;
    case DateSubconditionType.LAST_N_DAYS:
      return !!state.days && state.days > 0;
    case DateSubconditionType.ALL_TIME:
      return true;
  }
};

export const toModelDateSubcondition = (state: DateSubconditionState) => {
  const result: any = { type: state.type };

  switch (state.type) {
    case DateSubconditionType.ON_OR_BEFORE:
      if (state.beforeDate) {
        result.beforeDate = state.beforeDate.toISOString();
      }
      break;
    case DateSubconditionType.ON_OR_AFTER:
      if (state.afterDate) {
        result.afterDate = state.afterDate.toISOString();
      }
      break;
    case DateSubconditionType.BETWEEN:
      if (state.startDate) {
        result.startDate = state.startDate.toISOString();
      }
      if (state.endDate) {
        result.endDate = state.endDate.toISOString();
      }
      break;
    case DateSubconditionType.LAST_N_DAYS:
      result.days = state.days;
      break;
  }

  return result;
};

export const fromModelDateSubcondition = (
  model: DateSubcondition,
): DateSubconditionState => {
  const result: DateSubconditionState = {
    type: model.type || DateSubconditionType.ALL_TIME,
    beforeDate: null,
    afterDate: null,
    startDate: null,
    endDate: null,
    days: null,
  };

  switch (result.type) {
    case DateSubconditionType.ON_OR_BEFORE:
      result.beforeDate = model.beforeDate ? new Date(model.beforeDate) : null;
      break;
    case DateSubconditionType.ON_OR_AFTER:
      result.afterDate = model.afterDate ? new Date(model.afterDate) : null;
      break;
    case DateSubconditionType.BETWEEN:
      result.startDate = model.startDate ? new Date(model.startDate) : null;
      result.endDate = model.endDate ? new Date(model.endDate) : null;
      break;
    case DateSubconditionType.LAST_N_DAYS:
      result.days = model.days || null;
      break;
  }

  return result;
};

// Helper function to provide human-readable standalone description
export const getDateSubconditionDescription = (
  state: DateSubconditionState,
): string => {
  switch (state.type) {
    case DateSubconditionType.ON_OR_BEFORE:
      return state.beforeDate
        ? `On or before ${state.beforeDate.toLocaleDateString()}`
        : "On or before date";
    case DateSubconditionType.ON_OR_AFTER:
      return state.afterDate
        ? `On or after ${state.afterDate.toLocaleDateString()}`
        : "On or after date";
    case DateSubconditionType.BETWEEN:
      return state.startDate && state.endDate
        ? `Between ${state.startDate.toLocaleDateString()} and ${state.endDate.toLocaleDateString()}`
        : "Between dates";
    case DateSubconditionType.LAST_N_DAYS:
      return state.days ? `Last ${state.days} days` : "Last n days";
    default:
      return "All time";
  }
};

// Helper function to get date subcondition description suffix for conditions
export const getDateSubconditionSuffix = (
  state: DateSubconditionState,
): string => {
  // If it's all time, don't add anything
  if (state.type === DateSubconditionType.ALL_TIME) {
    return "";
  }

  // Otherwise, add the appropriate suffix based on the type
  switch (state.type) {
    case DateSubconditionType.LAST_N_DAYS:
      if (state.days) {
        return ` in the last ${state.days} days`;
      }
      break;
    case DateSubconditionType.ON_OR_AFTER:
      if (state.afterDate) {
        return ` on or after ${state.afterDate.toLocaleDateString("en-us", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`;
      }
      break;
    case DateSubconditionType.ON_OR_BEFORE:
      if (state.beforeDate) {
        return ` on or before ${state.beforeDate.toLocaleDateString("en-us", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`;
      }
      break;
    case DateSubconditionType.BETWEEN:
      if (state.startDate && state.endDate) {
        return ` between ${state.startDate.toLocaleDateString("en-us", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })} and ${state.endDate.toLocaleDateString("en-us", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}`;
      }
      break;
  }

  return "";
};
